import { useState, useEffect } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Building2,
  Home,
} from "lucide-react";
import toast from "react-hot-toast";
import { Toaster } from "react-hot-toast";
import api from "../interceptors.js";
import { useConfig } from "../../context/configContext.js";
import "../../App.css";

const Advertisers = () => {
  const { config } = useConfig();
  const cpoId = config.customer_id;

  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    advertiserId: "",
    advertiserName: "",
    isHouseAdvertiser: false,
  });

  const isMobile = window.innerWidth < 768;

  // ─── Fetch ───
  const fetchAdvertisers = async () => {
    if (!cpoId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/advertisers?cpo_id=${cpoId}`);
      setAdvertisers(res.data?.advertisers || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load advertisers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdvertisers();
  }, [cpoId]);

  // ─── Save (Create / Update) ───
  const handleSave = async () => {
    if (!form.advertiserName.trim()) {
      toast.error("Advertiser name is required");
      return;
    }

    try {
      if (editingId) {
        await api.put(`/api/advertisers/${editingId}`, {
          advertiserName: form.advertiserName,
          isHouseAdvertiser: form.isHouseAdvertiser,
        });
        toast.success("Advertiser updated");
      } else {
        if (!form.advertiserId.trim()) {
          toast.error("Advertiser ID is required");
          return;
        }
        await api.post("/api/advertisers", {
          advertiserId: form.advertiserId,
          advertiserName: form.advertiserName,
          cpoId: cpoId,
          isHouseAdvertiser: form.isHouseAdvertiser,
        });
        toast.success("Advertiser created");
      }
      resetForm();
      fetchAdvertisers();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Save failed");
    }
  };

  // ─── Delete ───
  const handleDelete = async (advId) => {
    if (!window.confirm(`Delete advertiser ${advId}? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/advertisers/${advId}`);
      toast.success("Advertiser deleted");
      fetchAdvertisers();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Delete failed");
    }
  };

  // ─── Edit ───
  const startEdit = (adv) => {
    setEditingId(adv.advertiser_id);
    setForm({
      advertiserId: adv.advertiser_id,
      advertiserName: adv.advertiser_name,
      isHouseAdvertiser: !!adv.is_house_advertiser,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ advertiserId: "", advertiserName: "", isHouseAdvertiser: false });
  };

  return (
    <div className="mx-auto w-full max-w-4xl text-slate-50 p-2 sm:p-4">
      <Toaster position={isMobile ? "bottom-center" : "top-right"} />

      {/* Header */}
      <div className="mb-6 rounded-lg border border-cyan-100/40 bg-white/95 p-5 text-slate-900 shadow-2xl shadow-blue-950/20 backdrop-blur sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-black uppercase text-sky-700">
              Brand Management
            </p>
            <h1 className="text-2xl font-black text-slate-900 md:text-3xl">
              Advertisers
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Manage advertising brands for{" "}
              <span className="font-semibold text-slate-700">{config.cpo_name || cpoId}</span>
            </p>
          </div>

          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-black text-white shadow transition hover:bg-sky-700"
          >
            <Plus size={18} />
            New Advertiser
          </button>
        </div>
      </div>

      {/* Create / Edit Form */}
      {showForm && (
        <div className="mb-6 rounded-lg border border-cyan-100/25 bg-slate-950/30 p-5 shadow-xl backdrop-blur animate-in fade-in">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-black text-white">
              {editingId ? "Edit Advertiser" : "New Advertiser"}
            </h2>
            <button onClick={resetForm} className="text-cyan-100/60 hover:text-white transition">
              <X size={20} />
            </button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {/* Advertiser ID */}
            <div>
              <label className="mb-1.5 block text-xs font-black uppercase text-cyan-100/70">
                Advertiser ID
              </label>
              <input
                type="text"
                placeholder="e.g. ADV001"
                value={form.advertiserId}
                disabled={!!editingId}
                onChange={(e) => setForm({ ...form, advertiserId: e.target.value })}
                className={`min-h-11 w-full rounded-lg border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white placeholder-white/40 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20 ${
                  editingId ? "cursor-not-allowed opacity-50" : ""
                }`}
              />
            </div>

            {/* Advertiser Name */}
            <div>
              <label className="mb-1.5 block text-xs font-black uppercase text-cyan-100/70">
                Advertiser Name
              </label>
              <input
                type="text"
                placeholder="e.g. KFC India"
                value={form.advertiserName}
                onChange={(e) => setForm({ ...form, advertiserName: e.target.value })}
                className="min-h-11 w-full rounded-lg border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white placeholder-white/40 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20"
              />
            </div>
          </div>

          {/* House Advertiser Toggle */}
          <label className="mt-4 inline-flex cursor-pointer items-center gap-3">
            <input
              type="checkbox"
              checked={form.isHouseAdvertiser}
              onChange={(e) => setForm({ ...form, isHouseAdvertiser: e.target.checked })}
              className="h-4 w-4 rounded accent-sky-500"
            />
            <span className="text-sm font-semibold text-cyan-100/80">
              House Advertiser (CPO's own ads)
            </span>
          </label>

          {/* Actions */}
          <div className="mt-5 flex gap-3">
            <button
              onClick={handleSave}
              className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-sky-600 px-5 py-2 text-sm font-black text-white shadow transition hover:bg-sky-700"
            >
              <Save size={16} />
              {editingId ? "Update" : "Create"}
            </button>
            <button
              onClick={resetForm}
              className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-white/10 px-5 py-2 text-sm font-black text-white/80 transition hover:bg-white/20"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Advertisers List */}
      <div className="rounded-lg border border-cyan-100/25 bg-slate-950/20 p-4 shadow-xl shadow-blue-950/10 backdrop-blur sm:p-5">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-xs font-black uppercase text-cyan-100/80">Directory</p>
            <h2 className="m-0 text-xl font-black text-white">
              All Advertisers
            </h2>
          </div>
          <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-black text-cyan-200">
            {advertisers.length} total
          </span>
        </div>

        {loading && (
          <p className="text-sm text-cyan-100/60 italic">Loading...</p>
        )}

        {!loading && advertisers.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Building2 size={48} className="mb-3 text-cyan-100/20" />
            <p className="text-sm font-semibold text-cyan-100/50">
              No advertisers yet
            </p>
            <p className="text-xs text-cyan-100/30">
              Click "New Advertiser" to add your first brand
            </p>
          </div>
        )}

        <div className="space-y-2">
          {advertisers.map((adv) => (
            <div
              key={adv.advertiser_id}
              className="group flex flex-wrap items-center gap-3 rounded-lg border border-cyan-100/15 bg-white/5 px-4 py-3 transition hover:border-cyan-100/30 hover:bg-white/10"
            >
              {/* Icon */}
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-900/50">
                {adv.is_house_advertiser ? (
                  <Home size={18} className="text-amber-400" />
                ) : (
                  <Building2 size={18} className="text-sky-400" />
                )}
              </div>

              {/* Info */}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-white">
                  {adv.advertiser_name}
                </p>
                <p className="text-xs text-cyan-100/50">
                  ID: {adv.advertiser_id}
                </p>
              </div>

              {/* Badges */}
              {adv.is_house_advertiser && (
                <span className="shrink-0 rounded-full bg-amber-900/40 px-2.5 py-0.5 text-[10px] font-black uppercase text-amber-300">
                  House
                </span>
              )}

              {/* Actions */}
              <div className="flex shrink-0 gap-1.5 opacity-0 transition group-hover:opacity-100">
                <button
                  onClick={() => startEdit(adv)}
                  className="rounded-lg bg-sky-600/20 p-2 text-sky-300 transition hover:bg-sky-600/40"
                  title="Edit"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => handleDelete(adv.advertiser_id)}
                  className="rounded-lg bg-red-600/20 p-2 text-red-400 transition hover:bg-red-600/40"
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Advertisers;
